import { useEffect, useMemo, useRef } from 'react';

import type { CategorizedAssessments } from '~/lib/types/survey/CategorizedAssessments';
import { extractSortedAssessments } from '~/lib/util/assessmentIndexList.util';

export const ASSESSMENT_CARD_TOP_LEVEL_ID = 'assessment-card';

export const useAssessmentListScrollObserver = (
  categorizedAssessments: CategorizedAssessments,
  mostRecentClickSurveyId: string | null,
  setSelectedSurveyId: (surveyId: string | null) => void,
) => {
  const intersectingElementsRef = useRef(new Map());
  const sortedSurveys = useMemo(() => {
    if (Object.keys(categorizedAssessments).length > 0) {
      return extractSortedAssessments(categorizedAssessments);
    }
    return [];
  }, [categorizedAssessments]);

  const surveyIdToIndexMap = useMemo(() => {
    return sortedSurveys.reduce((map, survey, index) => {
      map[survey.externalId] = index;
      return map;
    }, {});
  }, [sortedSurveys]);

  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const surveyId = entry.target.id.replace(
          `${ASSESSMENT_CARD_TOP_LEVEL_ID}__`,
          '',
        );

        if (entry.isIntersecting) {
          intersectingElementsRef.current.set(surveyId, entry);
        } else {
          intersectingElementsRef.current.delete(surveyId);
        }
      });

      const sortedIntersectingElements = Array.from(
        intersectingElementsRef.current.keys(),
      ).sort((a, b) => surveyIdToIndexMap[a] - surveyIdToIndexMap[b]);

      if (sortedIntersectingElements.length > 0) {
        if (
          mostRecentClickSurveyId &&
          intersectingElementsRef.current.has(mostRecentClickSurveyId)
        ) {
          setSelectedSurveyId(mostRecentClickSurveyId);
        } else {
          setSelectedSurveyId(sortedIntersectingElements[0]);
        }
      } else {
        setSelectedSurveyId(null);
      }
    };
    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.7,
      rootMargin: '10px 0px 10px 0px',
    });
    sortedSurveys.forEach((survey) => {
      const element = document.getElementById(
        `${ASSESSMENT_CARD_TOP_LEVEL_ID}__${survey.externalId}`,
      );
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sortedSurveys, surveyIdToIndexMap, mostRecentClickSurveyId]);
};
